import { apiV1 } from './apiClient';

/**
 * Servicio para gestión de configuración de empresas
 * Consume endpoints de backend/src/routes/v1/configuracionRoutes.js
 */
const configuracionApi = {
  /**
   * GET /api/v1/configuracion
   * Obtener configuración de la empresa del usuario autenticado
   * @returns {Promise<Object>} Configuración completa de la empresa
   */
  getConfiguracion: async () => {
    const response = await apiV1.get('/configuracion');
    return response.data;
  },

  /**
   * PUT /api/v1/configuracion
   * Actualizar configuración de la empresa
   * @param {Object} configData - Datos de configuración a actualizar
   * @param {boolean} [configData.soporte_gis] - Activar/desactivar GIS
   * @param {Array<number>} [configData.estados_activos] - IDs de estados activos
   * @param {Object} [configData.nombres_estados_custom] - Nombres personalizados
   * @returns {Promise<Object>} Configuración actualizada
   */
  updateConfiguracion: async (configData) => {
    const response = await apiV1.put('/configuracion', configData);
    return response.data;
  },

  /**
   * POST /api/v1/configuracion/aplicar-plantilla
   * Aplicar una plantilla predefinida a la empresa
   * @param {number} idPlantilla - ID de la plantilla a aplicar
   * @returns {Promise<Object>} Resultado de aplicar plantilla
   */
  aplicarPlantilla: async (idPlantilla) => {
    const response = await apiV1.post('/configuracion/aplicar-plantilla', {
      id_plantilla: idPlantilla
    });
    return response.data;
  },

  /**
   * PUT /api/v1/configuracion/personalizar-estados
   * Personalizar nombres de estados de la empresa
   * @param {Object} nombresCustom - Objeto con nombres personalizados { id_estado: nombre }
   * @returns {Promise<Object>} Configuración actualizada
   */
  personalizarEstados: async (nombresCustom) => {
    const response = await apiV1.put('/configuracion/personalizar-estados', {
      nombres_custom: nombresCustom
    });
    return response.data;
  },

  /**
   * PUT /api/v1/configuracion/gis
   * Configurar soporte GIS para la empresa
   * @param {boolean} soporteGis - Activar/desactivar GIS
   * @returns {Promise<Object>} Configuración actualizada
   */
  configurarGIS: async (soporteGis) => {
    const response = await apiV1.put('/configuracion/gis', {
      soporte_gis: soporteGis
    });
    return response.data;
  },

  /**
   * GET /api/v1/configuracion/estados-disponibles
   * Obtener estados configurados para la empresa
   * @returns {Promise<Array>} Lista de estados disponibles
   */
  getEstadosDisponibles: async () => {
    const response = await apiV1.get('/configuracion/estados-disponibles');
    return response.data;
  },

  /**
   * GET /api/v1/configuracion/historial
   * Obtener historial de cambios de configuración
   * @returns {Promise<Array>} Historial de cambios
   */
  getHistorial: async () => {
    const response = await apiV1.get('/configuracion/historial');
    return response.data;
  },

  // ============= MÉTODOS PARA ADMIN SISTEMA =============

  /**
   * GET /api/v1/configuracion/empresa/:id
   * Obtener configuración de una empresa específica (Admin Sistema)
   * @param {number} idEmpresa - ID de la empresa
   * @returns {Promise<Object>} Configuración de la empresa
   */
  getConfiguracionByEmpresa: async (idEmpresa) => {
    const response = await apiV1.get(`/configuracion/empresa/${idEmpresa}`);
    return response.data;
  },

  /**
   * POST /api/v1/configuracion/empresa/:id/aplicar-plantilla
   * Aplicar plantilla a una empresa específica (Admin Sistema)
   * @param {number} idEmpresa - ID de la empresa
   * @param {number} idPlantilla - ID de la plantilla
   * @returns {Promise<Object>} Configuración actualizada
   */
  aplicarPlantillaByEmpresa: async (idEmpresa, idPlantilla) => {
    const response = await apiV1.post(`/configuracion/empresa/${idEmpresa}/aplicar-plantilla`, {
      id_plantilla: idPlantilla
    });
    return response.data;
  },

  /**
   * PUT /api/v1/configuracion/empresa/:id/estados
   * Personalizar nombres de estados de una empresa específica (Admin Sistema)
   * @param {number} idEmpresa - ID de la empresa
   * @param {Object} nombresCustom - Nombres personalizados
   * @returns {Promise<Object>} Configuración actualizada
   */
  personalizarEstadosByEmpresa: async (idEmpresa, nombresCustom) => {
    const response = await apiV1.put(`/configuracion/empresa/${idEmpresa}/estados`, {
      nombres_custom: nombresCustom
    });
    return response.data;
  },

  /**
   * PUT /api/v1/configuracion/empresa/:id/gis
   * Configurar GIS de una empresa específica (Admin Sistema)
   * @param {number} idEmpresa - ID de la empresa
   * @param {boolean} soporteGis - Activar/desactivar GIS
   * @returns {Promise<Object>} Configuración actualizada
   */
  configurarGISByEmpresa: async (idEmpresa, soporteGis) => {
    const response = await apiV1.put(`/configuracion/empresa/${idEmpresa}/gis`, {
      soporte_gis: soporteGis
    });
    return response.data;
  }
};

export default configuracionApi;
